import React, { useState, useEffect } from 'react';
import { AmplifierProjectData } from './amplifier';
import { amplifierMqttService } from './AmplifierMqttService';
import { createEmptyAmplifierProjectData } from './remoteControlMapper';
import { useAmplifierI18n } from '../../i18n/amplifier/useAmplifierI18n';
import type { AmplifierLocaleKey } from '../../i18n/amplifier/locales';

interface CalibrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  amplifierProjectData?: AmplifierProjectData;
  deviceId: string;
}

export const CalibrationModal: React.FC<CalibrationModalProps> = ({ isOpen, onClose, onConfirm, amplifierProjectData, deviceId }) => {
  const { t } = useAmplifierI18n();
  const [activeTab, setActiveTab] = useState<'calibration' | 'voltage'>('calibration');
  const [formData, setFormData] = useState<AmplifierProjectData>(createEmptyAmplifierProjectData);

  // 当 amplifierProjectData 变化时，仅同步表单显示数据
  useEffect(() => {
    if (amplifierProjectData) {
      setFormData(amplifierProjectData);
    }
  }, [amplifierProjectData]);

  // 当前正在编辑的字段 key（如 'voltageCalibrationCoefficient' 或 'bandParam.vgs1.frequency'），失焦时据此下发 MQTT
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');

  // 数值展示：0 或 undefined/null 均显示为 "0"
  const displayNum = (n: number | undefined | null): string => String(n ?? 0);
  const displayUnitValue = (n: number | undefined | null, unit: string): string => `${displayNum(n)} ${unit}`;

  // 根据字段 key 从 formData 取当前值（支持顶层字段与 bandParam.vgsN.xxx）
  const getFormValue = (fieldKey: string): number => {
    if (fieldKey.startsWith('bandParam.')) {
      const parts = fieldKey.split('.');
      const vgsKey = parts[1] as keyof typeof formData.bandParam;
      const subKey = parts[2] as keyof (typeof formData.bandParam)['vgs1'];
      return (formData.bandParam[vgsKey] as unknown as Record<string, number>)?.[subKey] ?? 0;
    }
    return (formData as unknown as Record<string, number>)[fieldKey] ?? 0;
  };

  // 根据字段 key 和 value 构建下发用的部分 payload（深合并用）
  const buildPayloadForField = (fieldKey: string, value: number): Partial<AmplifierProjectData> => {
    if (!fieldKey.startsWith('bandParam.')) {
      return { [fieldKey]: value } as Partial<AmplifierProjectData>;
    }
    const parts = fieldKey.split('.');
    const vgsKey = parts[1] as keyof typeof formData.bandParam;
    const subKey = parts[2] as keyof (typeof formData.bandParam)['vgs1'];
    const currentVgs = formData.bandParam[vgsKey] as unknown as Record<string, number>;
    return {
      bandParam: {
        ...formData.bandParam,
        [vgsKey]: {
          ...currentVgs,
          [subKey]: value
        }
      }
    };
  };

  // 处理参数更新：仅下发 MQTT，不更新本地 formData（等 MQTT 回包后由 amplifierProjectData 驱动渲染）
  const handleParameterChange = async (newData: Partial<AmplifierProjectData>) => {
    try {
      const updatedData = { ...formData, ...newData } as AmplifierProjectData;
      await amplifierMqttService.sendProjectParametersDelta(deviceId, newData);
      console.log('CalibrationModal: 工程参数指令发送成功:', updatedData);
    } catch (error) {
      console.error('CalibrationModal: 工程参数指令发送失败:', error);
    }
  };

  // 输入框失焦时：将当前编辑值下发 MQTT，并清除编辑状态
  const handleFieldBlur = (fieldKey: string) => {
    if (editingField !== fieldKey) return;
    const num = parseFloat(editingValue) || 0;
    handleParameterChange(buildPayloadForField(fieldKey, num));
    setEditingField(null);
  };

  // 处理加减按钮点击事件（从 formData 取当前值，下发新值，不做乐观更新）
  const handleButtonClick = async (field: keyof AmplifierProjectData | string, delta: number) => {
    try {
      let currentValue: number;
      if (typeof field === 'string' && field.startsWith('bandParam.vgs1.')) {
        const subField = field.split('.')[2] as keyof typeof formData.bandParam.vgs1;
        currentValue = formData.bandParam.vgs1[subField] as number;
      } else {
        currentValue = formData[field as keyof AmplifierProjectData] as number;
      }
      const newValue = currentValue + delta;
      if (typeof field === 'string' && field.startsWith('bandParam.vgs1.')) {
        const subField = field.split('.')[2] as keyof typeof formData.bandParam.vgs1;
        handleParameterChange({
          bandParam: {
            ...formData.bandParam,
            vgs1: {
              ...formData.bandParam.vgs1,
              [subField]: newValue
            }
          }
        });
      } else {
        handleParameterChange({
          [field as keyof AmplifierProjectData]: newValue
        });
      }
    } catch (error) {
      console.error('CalibrationModal: 加减按钮点击失败:', error);
    }
  };

  // 频率段选项
  const bandOptions = [
    'BAND1', 'BAND2', 'BAND3', 'BAND4', 'BAND5',
    'BAND6', 'BAND7', 'BAND8', 'BAND9', 'BAND10',
    'BAND11', 'BAND12', 'BAND13', 'BAND14', 'BAND15',
    'BAND16', 'BAND17'
  ];
  const [selectedBand, setSelectedBand] = useState('BAND1');

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '8px',
        padding: '0',
        width: '90%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
      }}>


        {/* 标签页切换 */}
        <div style={{
          display: 'flex',
          backgroundColor: '#252525',
          borderBottom: '1px solid #333'
        }}>
          <button
            onClick={() => setActiveTab('calibration')}
            style={{
              padding: '8px 16px',
              backgroundColor: activeTab === 'calibration' ? '#333' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'calibration' ? '2px solid #007acc' : 'none',
              color: activeTab === 'calibration' ? 'white' : '#aaa',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: activeTab === 'calibration' ? '600' : 'normal',
              transition: 'all 0.2s ease'
            }}
          >
            {t('校准系数')}
          </button>
          <button
            onClick={() => setActiveTab('voltage')}
            style={{
              padding: '8px 16px',
              backgroundColor: activeTab === 'voltage' ? '#333' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'voltage' ? '2px solid #007acc' : 'none',
              color: activeTab === 'voltage' ? 'white' : '#aaa',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: activeTab === 'voltage' ? '600' : 'normal',
              transition: 'all 0.2s ease'
            }}
          >
            {t('其他')}
          </button>
        </div>

        {/* 内容区域 */}
        <div style={{
          flex: 1,
          padding: '20px',
          overflow: 'auto'
        }}>
          {/* 校准系数页面 */}
          {activeTab === 'calibration' && (
            <div>
              <div style={{ display: 'flex', gap: '24px', marginBottom: '24px' }}>
                {/* 左侧校准系数 */}
                <div style={{ flex: 1 }}>
                  {/* 频段选择 */}
                  <div style={{ marginBottom: '16px' }}>
                    <h3 style={{ color: '#007acc', marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>{t('频段校准系数')}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <label style={{ color: 'white', width: '80px' }}>{t('频段')}:</label>
                      <select style={{
                        flex: 1,
                        height: '29px',
                        backgroundColor: '#333',
                        border: '1px solid #444',
                        color: 'white',
                        borderRadius: '4px',
                        padding: '0 10px',
                        fontSize: '12px'
                      }} value={selectedBand} onChange={(e) => setSelectedBand(e.target.value)}>
                        {bandOptions.map((band, index) => (
                          <option key={index} value={band}>{band}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* 当前频段 VGS / VGS 频率 */}
                  {(() => {
                    const bandIndex = bandOptions.indexOf(selectedBand) + 1;
                    const vgsKey = `vgs${bandIndex}` as keyof typeof formData.bandParam;
                    const vgsData = formData.bandParam[vgsKey];
                    const vgsPowerKey = `bandParam.${vgsKey}.vgsPower`;
                    const frequencyKey = `bandParam.${vgsKey}.frequency`;
                    return (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', gap: '10px' }}>
                          <label style={{ color: 'white', width: '80px', fontSize: '12px' }}>{t('vgs:')}</label>
                          <input
                            style={{
                              flex: 1,
                              height: '29px',
                              backgroundColor: '#333',
                              border: '1px solid #444',
                              color: 'white',
                              borderRadius: '4px',
                              padding: '0 10px',
                              fontSize: '12px'
                            }}
                            value={editingField === vgsPowerKey ? editingValue : displayUnitValue(vgsData?.vgsPower, 'W')}
                            onFocus={() => { setEditingField(vgsPowerKey); setEditingValue(displayNum(vgsData?.vgsPower)); }}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={() => handleFieldBlur(vgsPowerKey)}
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <label style={{ color: 'white', width: '80px', fontSize: '12px' }}>{t('vgs频率:')}</label>
                          <input
                            style={{
                              flex: 1,
                              height: '29px',
                              backgroundColor: '#333',
                              border: '1px solid #444',
                              color: 'white',
                              borderRadius: '4px',
                              padding: '0 10px',
                              fontSize: '12px'
                            }}
                            value={editingField === frequencyKey ? editingValue : displayUnitValue(vgsData?.frequency, 'MHz')}
                            onFocus={() => { setEditingField(frequencyKey); setEditingValue(displayNum(vgsData?.frequency)); }}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={() => handleFieldBlur(frequencyKey)}
                          />
                        </div>
                      </div>
                    );
                  })()}

                  {/* 频段校准系数 */}
                  <div style={{ marginBottom: '16px' }}>
                    {([
                        '输入正向功率校准系数',
                        '输入反向功率校准系数',
                        '输出正向功率校准系数',
                        '输出反向功率校准系数'
                      ] as AmplifierLocaleKey[]).map((labelKey, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '10px',
                          gap: '10px'
                        }}>
                          <label style={{ color: 'white', width: '150px', fontSize: '12px' }}>{t(labelKey)}:</label>
                          <button style={{
                            width: '29px',
                            height: '29px',
                            backgroundColor: '#333',
                            border: '1px solid #444',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }} onClick={() => handleButtonClick(`bandParam.vgs1.${index === 0 ? 'inputPositive' : index === 1 ? 'inputNegative' : index === 2 ? 'outputPositive' : 'outputNegative'}`, -1)}>-</button>
                          <input style={{
                            flex: 1,
                            height: '29px',
                            backgroundColor: '#333',
                            border: '1px solid #444',
                            color: 'white',
                            borderRadius: '4px',
                            padding: '0 10px',
                            textAlign: 'center',
                            fontSize: '12px'
                          }}
                          value={index === 0 ? (editingField === 'bandParam.vgs1.inputPositive' ? editingValue : displayUnitValue(formData.bandParam.vgs1.inputPositive, 'W')) :
                                 index === 1 ? (editingField === 'bandParam.vgs1.inputNegative' ? editingValue : displayUnitValue(formData.bandParam.vgs1.inputNegative, 'W')) :
                                 index === 2 ? (editingField === 'bandParam.vgs1.outputPositive' ? editingValue : displayUnitValue(formData.bandParam.vgs1.outputPositive, 'W')) :
                                 (editingField === 'bandParam.vgs1.outputNegative' ? editingValue : displayUnitValue(formData.bandParam.vgs1.outputNegative, 'W'))}
                          onFocus={() => {
                            const key = index === 0 ? 'bandParam.vgs1.inputPositive' : index === 1 ? 'bandParam.vgs1.inputNegative' : index === 2 ? 'bandParam.vgs1.outputPositive' : 'bandParam.vgs1.outputNegative';
                            setEditingField(key);
                            setEditingValue(displayNum(getFormValue(key)));
                          }}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => handleFieldBlur(index === 0 ? 'bandParam.vgs1.inputPositive' : index === 1 ? 'bandParam.vgs1.inputNegative' : index === 2 ? 'bandParam.vgs1.outputPositive' : 'bandParam.vgs1.outputNegative')}
                          />
                          <button style={{
                            width: '29px',
                            height: '29px',
                            backgroundColor: '#333',
                            border: '1px solid #444',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }} onClick={() => handleButtonClick(`bandParam.vgs1.${index === 0 ? 'inputPositive' : index === 1 ? 'inputNegative' : index === 2 ? 'outputPositive' : 'outputNegative'}`, 1)}>+</button>
                        </div>
                      ))}
                  </div>

                  {/* 通用校准系数 */}
                  <div>
                    <h3 style={{ color: '#007acc', marginBottom: '10px', fontSize: '14px', fontWeight: '600' }}>{t('校准系数')}</h3>
                    {
                      ([
                        '电压校准系数',
                        '电流校准系数',
                        '频率校准系数'
                      ] as AmplifierLocaleKey[]).map((labelKey, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '10px',
                          gap: '10px'
                        }}>
                          <label style={{ color: 'white', width: '150px', fontSize: '12px' }}>{t(labelKey)}:</label>
                          <button style={{
                            width: '29px',
                            height: '29px',
                            backgroundColor: '#333',
                            border: '1px solid #444',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }} onClick={() => handleButtonClick(index === 0 ? 'voltageCalibrationCoefficient' : index === 1 ? 'currentCalibrationCoefficient' : 'frequencyMeasurementCalibrationCoefficient', -1)}>-</button>
                          <input style={{
                            flex: 1,
                            height: '29px',
                            backgroundColor: '#333',
                            border: '1px solid #444',
                            color: 'white',
                            borderRadius: '4px',
                            padding: '0 10px',
                            textAlign: 'center',
                            fontSize: '12px'
                          }}
                          value={index === 0 ? (editingField === 'voltageCalibrationCoefficient' ? editingValue : displayUnitValue(formData.voltageCalibrationCoefficient, 'V')) :
                                 index === 1 ? (editingField === 'currentCalibrationCoefficient' ? editingValue : displayUnitValue(formData.currentCalibrationCoefficient, 'A')) :
                                 (editingField === 'frequencyMeasurementCalibrationCoefficient' ? editingValue : displayUnitValue(formData.frequencyMeasurementCalibrationCoefficient, 'MHz'))}
                          onFocus={() => {
                            const key = index === 0 ? 'voltageCalibrationCoefficient' : index === 1 ? 'currentCalibrationCoefficient' : 'frequencyMeasurementCalibrationCoefficient';
                            setEditingField(key);
                            setEditingValue(displayNum(getFormValue(key)));
                          }}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => handleFieldBlur(index === 0 ? 'voltageCalibrationCoefficient' : index === 1 ? 'currentCalibrationCoefficient' : 'frequencyMeasurementCalibrationCoefficient')}
                          />
                          <button style={{
                            width: '29px',
                            height: '29px',
                            backgroundColor: '#333',
                            border: '1px solid #444',
                            color: 'white',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }} onClick={() => handleButtonClick(index === 0 ? 'voltageCalibrationCoefficient' : index === 1 ? 'currentCalibrationCoefficient' : 'frequencyMeasurementCalibrationCoefficient', 1)}>+</button>
                        </div>
                      ))
                    }
                  </div>
                </div>

                {/* 右侧电压参数 */}
                <div style={{ flex: 1 }}>
                  <h3 style={{ color: '#007acc', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>{t('电压')}</h3>
                  <div style={{ marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {([
                      { label: '输出正向功率比较电压' as AmplifierLocaleKey, field: 'outputPowerPositiveCompareVoltage' as const },
                      { label: '输出反向功率比较电压' as AmplifierLocaleKey, field: 'outputPowerNegativeCompareVoltage' as const },
                      { label: '输入反向功率比较电压' as AmplifierLocaleKey, field: 'inputPowerNegativeCompareVoltage' as const },
                      { label: '输入正向功率比较电压' as AmplifierLocaleKey, field: 'inputPowerPositiveCompareVoltage' as const },
                      { label: '过流比较电压' as AmplifierLocaleKey, field: 'overCurrentCompareVoltage' as const },
                      { label: '功率触发PTT电压' as AmplifierLocaleKey, field: 'powerTriggerPttVoltage' as const },
                      { label: '功放管输入功率比较电压' as AmplifierLocaleKey, field: 'powerInputPowerPositiveCompareVoltage' as const },
                      { label: '功放管输出功率比较电压' as AmplifierLocaleKey, field: 'powerOutputPowerPositiveCompareVoltage' as const }
                    ]).map((item, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label style={{ color: 'white', width: '180px', fontSize: '12px', flexShrink: 0 }}>{t(item.label)}:</label>
                        <input style={{
                          flex: 1,
                          height: '28px',
                          backgroundColor: '#333',
                          border: '1px solid #444',
                          color: 'white',
                          borderRadius: '4px',
                          padding: '0 10px',
                          fontSize: '12px'
                        }}
                        value={editingField === item.field ? editingValue : displayUnitValue(formData[item.field], 'V')}
                        onFocus={() => { setEditingField(item.field); setEditingValue(displayNum(formData[item.field])); }}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onBlur={() => handleFieldBlur(item.field)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 其他页面 */}
          {activeTab === 'voltage' && (
            <div style={{ padding: '0' }}>
              <h3 style={{ color: '#007acc', marginBottom: '15px', marginTop: '0', fontSize: '14px', fontWeight: '600' }}>{t('其他')}</h3>
              <div style={{ marginBottom: '15px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px 24px' }}>
                {([
                  { label: '驻波调谐值' as AmplifierLocaleKey, field: 'rfTuneValue' as const },
                  { label: '负载电阻' as AmplifierLocaleKey, field: 'protectionRf' as const },
                  { label: '保护电压' as AmplifierLocaleKey, field: 'protectionVoltage' as const },
                  { label: '保护电流' as AmplifierLocaleKey, field: 'protectionCurrent' as const },
                  { label: '保护温度' as AmplifierLocaleKey, field: 'protectionTemperature' as const },
                  { label: '频率测量值' as AmplifierLocaleKey, field: 'frequencyMeasurementCalibrationCoefficient' as const },
                  { label: '天调电容选择值' as AmplifierLocaleKey, field: 'protectionRf' as const },
                  { label: '天调电感选择值' as AmplifierLocaleKey, field: 'protectionRf' as const },
                  { label: '保护输入正向功率' as AmplifierLocaleKey, field: 'protectionInputPowerPositive' as const },
                  { label: '保护输入反向功率' as AmplifierLocaleKey, field: 'protectionInputPowerNegative' as const },
                  { label: '保护输出正向功率' as AmplifierLocaleKey, field: 'protectionOutputPowerPositive' as const },
                  { label: '保护输出反向功率' as AmplifierLocaleKey, field: 'protectionOutputPowerNegative' as const },
                  { label: '保护驻波比' as AmplifierLocaleKey, field: 'protectionRf' as const },
                  { label: '保护频率误差' as AmplifierLocaleKey, field: 'protectionFrequencyError' as const }
                ]).map((item, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <label style={{ color: 'white', width: '140px', flexShrink: 0, fontSize: '12px' }}>{t(item.label)}:</label>
                    <input style={{
                      flex: 1,
                      minWidth: 0,
                      height: '26.5px',
                      backgroundColor: '#333',
                      border: '1px solid #444',
                      color: 'white',
                      borderRadius: '4px',
                      padding: '0 10px',
                      fontSize: '12px'
                    }}
                    value={editingField === item.field ? editingValue : displayUnitValue(formData[item.field], '℃')}
                    onFocus={() => { setEditingField(item.field); setEditingValue(displayNum(formData[item.field])); }}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onBlur={() => handleFieldBlur(item.field)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 底部按钮 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '15px 20px',
          backgroundColor: '#2a2a2a',
          borderTop: '1px solid #333',
          borderRadius: '0 0 8px 8px'
        }}>
          <div style={{ color: '#888', fontSize: '12px' }}>{t('工程参数设置界面')}</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={{
              padding: '10px 24px',
              backgroundColor: '#333',
              border: '1px solid #444',
              color: 'white',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s ease'
            }} onClick={onClose}>{t('取消')}</button>
            <button style={{
              padding: '10px 24px',
              backgroundColor: '#007acc',
              border: '1px solid #0099ff',
              color: 'white',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }} onClick={onConfirm}>{t('确认')}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalibrationModal;